/**
 * Top pagination for the site search app (Deakin pagination__nav pattern).
 */
import { useCallback, useContext, RefObject } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  SearchAppContext,
  SearchAppDispatchContext,
} from '../../context/search-app/search-app-context';
import { SET_PAGE } from '../../context/search-app/search-app-actions';

export const BASE_CLASS = 'pagination__nav';

type PaginationItem =
  | { kind: 'page'; value: number }
  | { kind: 'ellipsis'; key: string };

function buildPaginationItems(
  current: number,
  total: number,
): PaginationItem[] {
  if (total < 2) {
    return [];
  }
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({
      kind: 'page' as const,
      value: i + 1,
    }));
  }

  const items: PaginationItem[] = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);

  items.push({ kind: 'page', value: 1 });
  if (left > 2) {
    items.push({ kind: 'ellipsis', key: 'start' });
  }
  for (let i = left; i <= right; i++) {
    if (i !== 1 && i !== total) {
      items.push({ kind: 'page', value: i });
    }
  }
  if (right < total - 1) {
    items.push({ kind: 'ellipsis', key: 'end' });
  }
  items.push({ kind: 'page', value: total });
  return items;
}

type Props = {
  searchRef: RefObject<HTMLDivElement | null>;
};

const SearchPagination = (props: Props) => {
  const { searchRef } = props;
  const { page, pageSize, totalResults, status } = useContext(SearchAppContext);
  const { dispatch } = useContext(SearchAppDispatchContext);

  const totalPages =
    pageSize > 0 ? Math.max(1, Math.ceil(totalResults / pageSize)) : 1;

  const goToPage = useCallback(
    (next: number) => {
      dispatch({ type: SET_PAGE, payload: next });
      if (searchRef.current) {
        searchRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [dispatch, searchRef],
  );

  if (status !== 'success' || totalPages < 2 || totalResults === 0) {
    return null;
  }

  const items = buildPaginationItems(page, totalPages);
  const zeroBasedPage = page - 1;

  return (
    <nav className={BASE_CLASS} aria-label="Pagination Navigation">
      <ul>
        <li role="presentation" className={page <= 1 ? 'disabled' : ''}>
          <button
            type="button"
            className="previous"
            aria-label="Previous"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
          >
            <ChevronLeftIcon fontSize="small" aria-hidden />
            <span className="search-pagination__sr-only">Previous</span>
          </button>
        </li>
        {items.map((item, index) =>
          item.kind === 'ellipsis' ? (
            <li key={`${item.key}-${index}`} role="presentation">
              <span className="ellipsis" aria-hidden>
                …
              </span>
            </li>
          ) : (
            <li
              key={item.value}
              role="presentation"
              className={item.value === page ? 'active' : ''}
            >
              {item.value === page ? (
                <span aria-current="page">{item.value}</span>
              ) : (
                <button
                  type="button"
                  aria-label={`Page ${item.value}`}
                  onClick={() => goToPage(item.value)}
                >
                  {item.value}
                </button>
              )}
            </li>
          ),
        )}
        <li
          role="presentation"
          className={zeroBasedPage >= totalPages - 1 ? 'disabled' : ''}
        >
          <button
            type="button"
            className="next"
            aria-label="Next"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
          >
            <ChevronRightIcon fontSize="small" aria-hidden />
            <span className="search-pagination__sr-only">Next</span>
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default SearchPagination;
